import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, CheckCircle2, Globe, History, KeyRound, Loader2, UserRound } from "lucide-react";
import { api } from "../../lib/api";
import { isPublic } from "../../lib/trainerProfile";
import TrainerLimoAccess from "./TrainerLimoAccess";
import TrainerAccounts from "./TrainerAccounts";
import PublicProfileDialog from "./trainers/PublicProfileDialog";
import PreviewButton from "./PreviewButton";

/**
 * Trainers, managed as people rather than as a property of a cohort.
 *
 * The first version of this made a trainer two text fields on the group, and
 * created one at Logiscool per cohort. Their API dedups on what we send, so
 * one person teaching three cohorts became three records there — on an account
 * holding a single trainer licence. A trainer exists before a cohort, outlasts
 * it, and teaches several; so they belong here.
 *
 * Creating somebody at Logiscool from this screen costs nothing. A licence is
 * spent when they are ASSIGNED to a cohort, which happens on the cohort. That
 * separation is the reason this tab is safe to use freely.
 */
const empty = { name: "", email: "", phone: "", notes: "" };

/**
 * The Logiscool record this person still owns themselves, if any.
 *
 * Migration (24.09.2026) copied those fields onto an account and left them on
 * the trainer, so a migrated person showed the same record twice on one
 * screen — once as an account above, once with its own buttons here. The
 * account is where it belongs now, so a migrated trainer has none of their
 * own; only the people from before migration keep theirs.
 */
export function legacyLimoId(trainer, sandbox = false) {
  const t = trainer || {};
  if (t.migrated_account_id) return null;
  return (sandbox ? t.limo_test_trainer_id : t.limo_trainer_id) || null;
}

export default function TrainersTab({ canMutate = true, canPreview = false }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sandbox, setSandbox] = useState(false);
  // The Logiscool accounts, so a trainer row can say which one they hold.
  const [accounts, setAccounts] = useState([]);
  const [history, setHistory] = useState(null);
  // Asking for a seat from the person's row: the picker itself lives in the
  // licences section, which is where the six accounts are.
  const [assignRequest, setAssignRequest] = useState(null);
  // The face on a cohort's card (16.10.2026).
  const [profileFor, setProfileFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        api.get("/admin/trainers"),
        api.get("/admin/trainer-accounts").catch(() => ({ data: [] })),
      ]);
      setAccounts(a.data || []);
      setRows(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut încărca trainerii.");
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Numele și emailul sunt obligatorii.");
      return;
    }
    setBusy(true);
    try {
      if (form.id) {
        await api.put(`/admin/trainers/${form.id}`, form);
        toast.success("Trainer actualizat");
      } else {
        await api.post("/admin/trainers", form);
        toast.success("Trainer adăugat");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Ștergi trainerul ${t.name}?`)) return;
    try {
      await api.delete(`/admin/trainers/${t.id}`);
      toast.success("Trainer șters");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut șterge.");
    }
  };

  const clearAssign = useCallback(() => setAssignRequest(null), []);

  const limoIdOf = (t) => legacyLimoId(t, sandbox);
  // Somebody from before the accounts existed, whose record is still their own.
  const hasLegacy = (rows || []).some((t) => legacyLimoId(t, false) || legacyLimoId(t, true));
  const date = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO") : "");
  const accountOf = (t) => accounts.find((a) => a.holder?.trainer_id === t.id) || null;

  // What this person has held, and when — the question that made us keep a
  // history in the first place (24.09.2026).
  const showTrainerHistory = async (t) => {
    try {
      const r = await api.get("/admin/trainer-accounts/history", { params: { trainer_id: t.id } });
      setHistory({ name: t.name, items: r.data.items || [] });
    } catch {
      toast.error("Nu am putut citi istoricul.");
    }
  };

  return (
    <div className="space-y-5" data-testid="trainers-tab">
      <div>
        <h2 className="font-display text-xl font-bold text-[#373A45]">Traineri</h2>
        <p className="mt-0.5 text-sm text-[#6B7280]">
          Două lucruri diferite, pe același ecran: <strong className="font-semibold text-[#373A45]">oamenii</strong> care
          predau și cele șase <strong className="font-semibold text-[#373A45]">licențe Logiscool</strong> pe care le
          împrumutăm. Un formator predă cu licența pe care o ține, la toate grupele lui.
        </p>
      </div>

      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid="trainers-section">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-[#373A45]">Formatori</h3>
            <p className="mt-1 text-xs text-[#6B7280]">
              Persoanele care predau. Adaugi câți vrei — un formator nu costă nicio licență;
              licența se consumă abia când contul lui e pus pe o grupă.
            </p>
          </div>
          {canMutate && (
            <button
              onClick={() => setForm({ ...empty })}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#7A4986]"
              data-testid="trainer-add"
            >
              <Plus size={14} /> Formator nou
            </button>
          )}
        </div>

        {hasLegacy && (
          /* Only somebody who still has their OWN Logiscool record, from before
             the accounts existed, has anything to rehearse here. */
          <label className="mt-3 flex items-center gap-2 text-xs text-[#373A45] cursor-pointer">
            <input
              type="checkbox"
              checked={sandbox}
              onChange={(e) => setSandbox(e.target.checked)}
              className="h-4 w-4 accent-[#965EA7]"
              data-testid="trainers-sandbox"
            />
            <span>
              Mod de probă
              <span className="text-[#6B7280]"> — pentru conturile personale rămase de dinainte de licențe.</span>
            </span>
          </label>
        )}

        {history && (
          <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6]/60 p-3" data-testid="trainer-history">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#373A45]">
                Conturi ținute de {history.name}
              </div>
              <button type="button" onClick={() => setHistory(null)} className="text-[#6B7280]" aria-label="Închide">
                <X size={14} />
              </button>
            </div>
            {history.items.length === 0 ? (
              <p className="mt-2 text-xs text-[#6B7280]">Nu a ținut niciun cont Logiscool.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-xs">
                {history.items.map((h) => (
                  <li key={h.id} className="flex flex-wrap justify-between gap-2 border-b border-[#E5E7EB] pb-1">
                    <span className="font-medium text-[#373A45]">{h.account_label}</span>
                    <span className="text-[#6B7280]">
                      {new Date(h.from).toLocaleDateString("ro-RO")} –{" "}
                      {h.to ? new Date(h.to).toLocaleDateString("ro-RO") : "acum"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {rows === null && (
          <div className="mt-3 flex items-center gap-2 py-6 text-sm text-[#6B7280]">
            <Loader2 size={16} className="animate-spin" /> Se încarcă…
          </div>
        )}

        {rows?.length === 0 && (
          <div className="mt-3 rounded-xl border border-dashed border-[#E5E7EB] p-6 text-center text-sm text-[#6B7280]">
            Niciun formator încă. Adaugă-i pe cei care predau; licențele le dai după aceea.
          </div>
        )}

        {rows?.length > 0 && (
          <div className="mt-3 overflow-x-auto rounded-xl border border-[#E5E7EB]">
            <table className="w-full text-sm">
              <thead className="text-[#6B7280] uppercase tracking-wide text-xs bg-[#F5F6F6]">
                <tr>
                  <th className="text-left px-4 py-2.5">Nume</th>
                  <th className="text-left px-4 py-2.5">Email</th>
                  <th className="text-right px-4 py-2.5">Grupe</th>
                  <th className="text-left px-4 py-2.5">Cont Logiscool</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-t border-[#E5E7EB]" data-testid={`trainer-row-${t.id}`}>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setForm({ ...t })}
                          className="text-left font-medium text-[#373A45] hover:text-[#965EA7]"
                        >
                          {t.name}
                        </button>
                        {canPreview && t.active !== false && (
                          <PreviewButton target={{ trainerId: t.id }} label="Vezi portalul ca formatorul" iconOnly
                            testid={`trainer-preview-${t.id}`} />
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => setProfileFor(t)}
                        className={`mt-0.5 flex items-center gap-1 text-[11px] font-semibold ${isPublic(t)
                          ? "text-[#2F6B4E]" : "text-[#7A4986] hover:text-[#965EA7]"}`}
                        data-testid={`trainer-profile-open-${t.id}`}
                      >
                        <Globe size={11} /> {isPublic(t) ? "Pe site" : "Profil pe site"}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-[#6B7280]">{t.email}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#6B7280]">
                      {t.group_count}
                    </td>
                    <td className="px-4 py-2.5">
                      {/* From 24.09.2026 the Logiscool side belongs to the
                          accounts above; a trainer who still has their own
                          record from before keeps its buttons here. */}
                      {limoIdOf(t) ? (
                        <>
                          <span className="inline-flex items-center gap-1 text-[#2F6B4E] text-xs font-semibold">
                            <CheckCircle2 size={13} /> cont personal, de dinainte{sandbox ? " (probă)" : ""}
                          </span>
                          <TrainerLimoAccess key={`${t.id}-${sandbox}`} trainer={t} sandbox={sandbox} canMutate={canMutate} />
                        </>
                      ) : accountOf(t) ? (
                        <div data-testid={`trainer-account-hint-${t.id}`}>
                          <div className="text-[#373A45]">{accountOf(t).label}</div>
                          <div className="text-[11px] text-[#6B7280]">din {date(accountOf(t).holder?.since)}</div>
                        </div>
                      ) : canMutate ? (
                        <button
                          type="button"
                          onClick={() => setAssignRequest({ trainer: t, at: Date.now() })}
                          className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] px-2.5 py-0.5 text-[11px] font-semibold text-[#373A45] hover:bg-[#F5F6F6]"
                          data-testid={`trainer-account-hint-${t.id}`}
                        >
                          <KeyRound size={11} /> Dă-i o licență
                        </button>
                      ) : (
                        <span className="text-xs text-[#6B7280]" data-testid={`trainer-account-hint-${t.id}`}>
                          fără licență
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => showTrainerHistory(t)}
                        className="mr-1 rounded-lg p-1.5 text-[#6B7280] hover:bg-[#F5F6F6]"
                        title="Ce conturi Logiscool a ținut"
                        data-testid={`trainer-history-${t.id}`}
                      >
                        <History size={15} />
                      </button>
                      {canMutate && (
                        <button
                          onClick={() => remove(t)}
                          className="text-[#9b1f2c] hover:bg-[#E63946]/10 rounded-lg p-1.5"
                          title={t.group_count ? "Are grupe atribuite" : "Șterge"}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {profileFor && (
        <PublicProfileDialog
          trainer={profileFor}
          canMutate={canMutate}
          onClose={() => setProfileFor(null)}
          onChanged={load}
        />
      )}

      <TrainerAccounts trainers={rows || []} canMutate={canMutate} onChanged={load}
        assignRequest={assignRequest} onAssignHandled={clearAssign} />

      {form && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setForm(null)}>
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-md"
          >
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h3 className="font-display text-lg font-bold text-[#373A45] inline-flex items-center gap-2">
                <UserRound size={18} /> {form.id ? "Editează trainer" : "Trainer nou"}
              </h3>
              <button type="button" onClick={() => setForm(null)} className="text-[#6B7280] hover:text-[#373A45]">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Nume complet">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm"
                  data-testid="trainer-form-name"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={Boolean(legacyLimoId(form))}
                  className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm disabled:opacity-60"
                  data-testid="trainer-form-email"
                />
                {legacyLimoId(form) && (
                  /* Their record is keyed to this person. Changing the address
                     here would disagree with what Logiscool holds, and nothing
                     in this app would show the divergence. */
                  <span className="block mt-1 text-[11px] text-[#6B7280]">
                    Trainerul există în Logiscool — emailul nu mai poate fi schimbat de aici.
                  </span>
                )}
              </Field>
              <Field label="Telefon (opțional)">
                <input
                  value={form.phone || ""}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Notițe interne">
                <textarea
                  rows={3}
                  value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm resize-none"
                />
              </Field>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-[#E5E7EB] bg-[#F5F6F6]/60">
              <button type="button" onClick={() => setForm(null)} className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] hover:bg-white">
                Anulează
              </button>
              <button type="submit" disabled={busy} className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-60 text-white px-5 py-2 text-sm font-semibold">
                {busy ? "Se salvează…" : "Salvează"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
